import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import {
  Grid,
  TextField,
  InputAdornment,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  FormControlLabel,
  Checkbox,
  Button,
  Box,
} from '@mui/material';
import { useApiGet } from '../../../utils/httpClientTs';
import { getRosterDuesList } from '../../../endpoints/rosterDues';
import {
  RosterDuesListParams,
  RosterDuesListItem,
} from '../../../types/rosterDues';
import ServerSideTable, {
  ServerSideTableColumn,
} from '../../../Components/ServerSideTable';
import SPDSelector from '../../../Components/SPDSelector';
import { SortOrder } from '../../../types/table';
import { local_chapter_roster_dues_statuses } from '../../../utils/variables';
// TODO: Add spd_roster_dues_statuses to variables.js when available

interface RosterDuesListProps {
  formType?: 'local_chapter' | 'spd';
}

const RosterDuesList: React.FC<RosterDuesListProps> = ({
  formType = 'local_chapter',
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const updateSearchParams = useCallback(
    (key: string, value: string) => {
      if (!value || value === '') {
        searchParams.delete(key);
      } else {
        searchParams.set(key, value);
      }
      setSearchParams(searchParams);
    },
    [searchParams, setSearchParams]
  );

  const [order, setOrder] = useState<SortOrder>('asc');
  const [orderBy, setOrderBy] = useState<keyof RosterDuesListItem>(
    formType === 'spd' ? 'spd_chapter_name' : 'local_chapter_name'
  );
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(25);
  const [search, setSearch] = useState<string>(
    searchParams.get('search') || ''
  );
  const [delaySearch, setDelaySearch] = useState<string>(
    searchParams.get('search') || ''
  );
  const [spdSelected, setSpdSelected] = useState<string>(
    searchParams.get('spd') || ''
  );
  const [statusId, setStatusId] = useState<string>(
    searchParams.get('status_id') || ''
  );
  const [assignedToMe, setAssignedToMe] = useState<boolean>(
    searchParams.get('assigned_to_me') === 'true'
  );
  const currentUserId = localStorage.getItem('user_id');

  // need to update search params when search changes
  useEffect(() => {
    updateSearchParams('search', search);
  }, [search, updateSearchParams]);

  // need to update search params when spdSelected changes
  useEffect(() => {
    updateSearchParams('spd', spdSelected);
  }, [spdSelected, updateSearchParams]);

  // need to update search params when statusId changes
  useEffect(() => {
    updateSearchParams('status_id', statusId);
  }, [statusId, updateSearchParams]);

  // need to update search params when assignedToMe changes
  useEffect(() => {
    updateSearchParams('assigned_to_me', assignedToMe ? 'true' : '');
  }, [assignedToMe, updateSearchParams]);

  // Update default sort order when formType changes
  useEffect(() => {
    setOrderBy(formType === 'spd' ? 'spd_chapter_name' : 'local_chapter_name');
  }, [formType]);

  const params: RosterDuesListParams = {
    page: page + 1, // API uses 1-based indexing
    page_size: rowsPerPage,
    // year is omitted to let the server calculate it based on current membership year
    search: search || undefined,
    order_by: orderBy || undefined,
    order: order || undefined,
    spd: spdSelected || undefined,
    status_id: statusId || undefined,
    assigned_to_me: assignedToMe ? 'true' : undefined,
    form_type: formType,
  };

  const { data, isLoading } = useApiGet(
    [
      'roster-dues-list',
      formType,
      rowsPerPage,
      page,
      orderBy,
      order,
      search,
      spdSelected,
      statusId,
      assignedToMe,
    ],
    () => getRosterDuesList(params),
    {
      enabled: true,
      retry: 1,
      refetchOnMount: true,
      refetchOnWindowFocus: false,
    }
  );

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setSearch(delaySearch);
      setPage(0); // Reset to first page when search changes
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [delaySearch]);

  const handleClick = (form: RosterDuesListItem) => {
    if (formType === 'spd') {
      // SPD forms go to chapter list
      navigate(`/annual-reports/roster-dues/${form.id}/chapters`);
    } else {
      // Local chapter forms go to member list first
      navigate(`/annual-reports/roster-dues/${form.id}/members`);
    }
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const handleSort = (property: keyof RosterDuesListItem) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
    setPage(0);
  };

  const columns: ServerSideTableColumn<RosterDuesListItem>[] = [
    {
      id: formType === 'spd' ? 'spd_chapter_name' : 'local_chapter_name',
      label: formType === 'spd' ? 'SPD Name' : 'Chapter Name',
      width: '50%',
      firstCell: true,
      render: (value, row) => {
        const displayName =
          formType === 'spd' ? row.spd_chapter_name : row.local_chapter_name;
        return (
          <Typography variant="body2">
            {displayName}
            {row.assigned_to_id === currentUserId && (
              <Typography
                component="span"
                variant="caption"
                sx={{
                  ml: 1,
                  px: 1,
                  py: 0.25,
                  backgroundColor: 'primary.light',
                  color: 'primary.contrastText',
                  borderRadius: 1,
                  fontWeight: 'bold',
                }}
              >
                Assigned to Me
              </Typography>
            )}
          </Typography>
        );
      },
    },
    {
      id: 'assigned_to_name',
      label: 'Assigned To',
      width: '20%',
    },
    {
      id: 'status_name',
      label: 'Status',
      width: '30%',
    },
  ];

  const getRowStyle = (row: RosterDuesListItem) => {
    if (row.assigned_to_id === currentUserId) {
      return {
        backgroundColor: 'action.selected',
        borderLeft: '4px solid',
        borderLeftColor: 'primary.main',
      };
    }
    return {};
  };

  return (
    <Grid container paddingLeft={2} paddingRight={2}>
      <Grid item xs={12} padding={1}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 2,
          }}
        >
          <Button
            variant="outlined"
            onClick={() => navigate('/annual-reports/roster-dues')}
            sx={{ mr: 2 }}
          >
            Back
          </Button>
          <Typography
            variant="h4"
            color={'primary'}
            sx={{ flex: 1, textAlign: 'center' }}
          >
            {formType === 'spd'
              ? 'SPD Roster & Dues Forms'
              : 'Local Chapter Roster & Dues Forms'}
          </Typography>
          <Box sx={{ width: 100 }} /> {/* Spacer for centering */}
        </Box>
      </Grid>
      <Grid container spacing={2}>
        <Grid
          item
          xs={12}
          sm={6}
          md={formType === 'spd' ? 4 : 3}
          paddingBottom={3}
        >
          <TextField
            label="Search"
            id="search-field"
            size="small"
            value={delaySearch}
            fullWidth={true}
            onChange={(event) => setDelaySearch(event.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        {formType === 'local_chapter' && (
          <Grid item xs={12} sm={6} md={3} paddingBottom={3}>
            <SPDSelector
              spd={spdSelected}
              handleSpdSelect={(setSpd) => {
                setSpdSelected(setSpd);
              }}
              reportId="roster-dues"
              all={true}
            />
          </Grid>
        )}
        <Grid item xs={12} sm={6} md={3} paddingBottom={3}>
          <FormControl fullWidth size="small">
            <InputLabel id="status-filter-label">Status</InputLabel>
            <Select
              labelId="status-filter-label"
              id="status-filter"
              value={statusId}
              label="Status"
              onChange={(e: SelectChangeEvent<string>) => {
                setStatusId(e.target.value);
                setPage(0);
              }}
            >
              <MenuItem value="">
                <em>All</em>
              </MenuItem>
              {local_chapter_roster_dues_statuses.map((status) => (
                <MenuItem key={status.id} value={status.id}>
                  {status.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={3} paddingBottom={3}>
          <FormControlLabel
            control={
              <Checkbox
                checked={assignedToMe}
                onChange={(e) => {
                  setAssignedToMe(e.target.checked);
                  setPage(0);
                }}
              />
            }
            label="Assigned to Me"
          />
        </Grid>
      </Grid>
      <Paper sx={{ p: 2, width: '100%' }}>
        <ServerSideTable
          data={data?.data || []}
          columns={columns}
          loading={isLoading}
          page={page}
          rowsPerPage={rowsPerPage}
          totalCount={data?.pagination?.total_count || 0}
          totalPages={data?.pagination?.total_pages || 0}
          hasNext={data?.pagination?.has_next || false}
          hasPrev={data?.pagination?.has_prev || false}
          order={order}
          orderBy={String(orderBy)}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          onSort={handleSort}
          onRowClick={handleClick}
          emptyMessage="No roster and dues forms found."
          getRowStyle={getRowStyle}
        />
      </Paper>
    </Grid>
  );
};

export default RosterDuesList;
